// SEQ-180 phase 2, React re-review MEDIUM: what the reorder RPC's return
// value has to satisfy before the client believes the order was saved.
//
// `pnl_dashboard_widgets_reorder(p_dashboard_id uuid, p_widget_ids uuid[])`
// returns the number of rows it renumbered. That count is the only evidence
// the client gets that the write did what was asked, and it is NOT the same
// question as "did the call error".
//
// The three ways a call can come back non-erroring and still be wrong:
//
//  1. Fewer rows than ids. RLS matched some rows and not others, or a widget
//     was deleted by someone else between the drag and the drop. The order on
//     screen is now a claim about rows that were never written.
//  2. More rows than ids. The function touched something outside the list it
//     was given, which should be impossible and is worth failing loudly on
//     rather than trusting.
//  3. No count at all. A null, a string, an object: the function did not
//     answer the question, so the client has no evidence either way.
//
// All three are failures. The caller re-fetches rather than keeping an
// optimistic order it can no longer vouch for, which is the same rule the
// rest of this feature follows: never revert silently, and never claim a save
// that was not proven.
//
// Pure module, no React and no supabase import, so it is provable under Deno.

export interface ReorderCountOk {
  ok: true;
  count: number;
}

export interface ReorderCountFailure {
  ok: false;
  reason: "no_count" | "count_mismatch";
  count: number | null;
  expected: number;
  /** Shown to the user, so it says what to do rather than what the number was. */
  message: string;
}

export type ReorderCountResult = ReorderCountOk | ReorderCountFailure;

const PARTIAL_MESSAGE =
  "The new order was not saved in full, so the widgets have been reloaded to match the server. " +
  "Someone may have changed this dashboard at the same time.";

const NO_COUNT_MESSAGE =
  "The new order could not be confirmed, so the widgets have been reloaded to match the server.";

/**
 * Reads the RPC's returned row count and decides whether the reorder is
 * proven. `expected` is the length of the id array that was sent.
 *
 * A numeric string is accepted as well as a number: PostgREST returns a bare
 * scalar for a `RETURNS integer` function, and has been known to serialise
 * integer types as strings depending on the column type it infers. Anything
 * that is not a whole number is not a count.
 */
export function checkReorderCount(returned: unknown, expected: number): ReorderCountResult {
  const count = toWholeNumber(returned);

  if (count === null) {
    return { ok: false, reason: "no_count", count: null, expected, message: NO_COUNT_MESSAGE };
  }
  if (count !== expected) {
    return { ok: false, reason: "count_mismatch", count, expected, message: PARTIAL_MESSAGE };
  }
  return { ok: true, count };
}

function toWholeNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  }
  return null;
}
